import { useCallback, useEffect, useMemo } from 'react'
import { unstable_useBlocker as useBlocker, useNavigate, useLocation } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { F0ActionBar, RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import DOMPurify from 'dompurify'
import { PostCommentsFragment, PostDataFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import { useSection } from 'modules/core/components/SectionContainer'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import { track } from 'modules/core/lib/Tracking'

import Comments from 'modules/posts/components/Comments'
import Reactions from 'modules/posts/components/Reactions'
import Title from 'modules/posts/components/Title'
import Visits from 'modules/posts/components/Visits'
import { useCompletePostAction } from 'modules/posts/hooks/useCompletePostAction'
import { isVideo } from 'modules/posts/lib/isVideo'
import visits from 'modules/posts/store/collections/visits'

import AttachedFile from './Attachments'
import Event from './Event'
import PostActions from './PostActions'

interface Props {
  post: PostDataFragment & PostCommentsFragment
  fetchMoreComments: () => void
  hasMoreComments: boolean
  canManagePosts: boolean
  canSeeVisits: boolean
}

const Card = ({ post }: Pick<Props, 'post'>) => {
  const { title, description, author } = post
  const localeData = moment.localeData()
  const format = localeData.longDateFormat('LL')
  const publishedAt = moment.parseZone(post.publishedAt).format(format)

  useAsyncData(() => visits.fetch({ data: { post_ids: [post.id] } }), [post.id])

  return (
    <>
      <Box paddingBottom="s48" aria-label="This is the post title">
        <Title
          label={
            <Text size="500" color="grey1000" weight="medium" overflowWrap>
              {title && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(title),
                  }}
                />
              )}
            </Text>
          }
          type="primary"
          separator
          color="red"
        />
      </Box>
      <Event post={post} />
      <Box aria-label="This is the post description">
        <Text.Normal overflowWrap>
          {description && <RichTextDisplay content={description} />}
        </Text.Normal>
        <Box aria-label="These are the post attachments">
          {post.savedAttachmentsConnection.edges.length > 0 && (
            <Box flexDirection="row" flexWrap="wrap">
              {post.savedAttachmentsConnection.edges.map(({ node: attachment }, index) => (
                <AttachedFile key={index} attachment={attachment} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Box paddingTop="s48" aria-label="This shows the author of the post">
        {!!author && <>{author.fullName} — </>}
        <>{publishedAt}</>
      </Box>
    </>
  )
}

const CommentsAndReactions = ({
  post,
  fetchMoreComments,
  hasMoreComments,
  canManagePosts,
}: Props) => {
  const { allowCommentsAndReactions } = post
  if (!allowCommentsAndReactions) return null

  return (
    <Box aria-label="These are the comments and reactions of a post">
      <Box paddingTop="s16" paddingBottom="s64">
        <Reactions post={post} />
      </Box>
      <Comments
        post={post}
        fetchMoreComments={fetchMoreComments}
        hasMoreComments={hasMoreComments}
        canManagePosts={canManagePosts}
      />
    </Box>
  )
}

const PostCard = ({
  post,
  fetchMoreComments,
  hasMoreComments,
  canManagePosts,
  canSeeVisits,
}: Props) => {
  const {
    access: { id: currentAccessId },
    employee: { id: currentEmployeeId },
  } = useCurrentSession()
  const { author } = post
  const canEdit = canManagePosts || currentAccessId === author?.id

  const { coverImageUrl } = post

  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { level } = useSection()
  const hasVideo = isVideo(coverImageUrl)

  const postActionEdge = post.postActionsConnection?.edges?.[0]
  const postActionId = postActionEdge?.node?.id ?? null
  const hasAction = postActionId !== null
  const requiresAction = post.allRequiredActionsCompletedByCurrentUser === false && hasAction

  const {
    completeAction,
    loading: actionLoading,
    completed: actionCompleted,
  } = useCompletePostAction()

  const pendingAcknowledgement = requiresAction && !actionCompleted
  const isCompleted =
    hasAction && (actionCompleted || post.allRequiredActionsCompletedByCurrentUser === true)

  const acknowledgedAtLabel = useMemo(() => {
    if (!isCompleted) return ''

    const completions = postActionEdge?.node?.postActionCompletions ?? []
    const myCompletion = completions.find((c) => c.employeeId === currentEmployeeId)

    if (myCompletion?.createdAt) {
      const m = moment.parseZone(myCompletion.createdAt)
      const localeData = moment.localeData()
      const dateFormat = localeData.longDateFormat('LL')
      return i18n.t('posts.communities.post.acknowledged_on', {
        date: m.format(dateFormat),
        time: m.format('HH:mm'),
      })
    }

    return i18n.t('posts.communities.post.acknowledged_on', {
      date: moment().format(moment.localeData().longDateFormat('LL')),
      time: moment().format('HH:mm'),
    })
  }, [isCompleted, postActionEdge, currentEmployeeId])

  const showAcknowledgeBar = pendingAcknowledgement || isCompleted

  const blocker = useBlocker(pendingAcknowledgement)

  useEffect(() => {
    track('Post view', {
      coming_from: pathname,
      page_level: level,
      page_url: window.location.href,
      page_title: document.title,
    })
  }, [pathname])

  useEffect(() => {
    if (!pendingAcknowledgement) return

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }

    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [pendingAcknowledgement])

  const handleAcknowledge = useCallback(() => {
    if (postActionId !== null) {
      completeAction(postActionId)
    }
  }, [postActionId, completeAction])

  const handleAcknowledgeLater = useCallback(() => {
    navigate(-1)
  }, [navigate])

  return (
    <Box aria-label="This is the page that holds the whole post" overflowY="auto">
      <Box width="full" gap="s40" aria-label="This is the image of the post" alignItems="center">
        <Box width="s600" alignItems="flexEnd">
          {canEdit && <PostActions post={post} />}
        </Box>
        {coverImageUrl && !hasVideo && (
          <Box alignItems="center" borderRadius={{ all: 'abs016' }}>
            <img
              src={coverImageUrl}
              loading="lazy"
              style={{
                margin: '24px 0',
                borderRadius: 'inherit',
                width: '100%',
                maxHeight: 300,
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
        {coverImageUrl && hasVideo && (
          <video controls width="600px" style={{ borderRadius: '8px' }}>
            <source src={coverImageUrl} />
          </video>
        )}
        <Box
          style={{
            width: '600px',
            margin: '0 auto',
            paddingBottom: showAcknowledgeBar ? '5rem' : undefined,
          }}
          aria-label="This shows the content of the post with content, reactions, visits and comments"
        >
          <Card post={post} />
          <Box paddingY="s16" alignItems="flexStart">
            <Visits withText post={post} canSeeVisits={canSeeVisits} />
          </Box>
          <CommentsAndReactions
            post={post}
            fetchMoreComments={fetchMoreComments}
            hasMoreComments={hasMoreComments}
            canManagePosts={canManagePosts}
            canSeeVisits={canSeeVisits}
          />
        </Box>
      </Box>

      <F0ActionBar
        isOpen={showAcknowledgeBar}
        label={
          isCompleted ? acknowledgedAtLabel : i18n.t('posts.communities.post.acknowledge_required')
        }
        status={isCompleted ? 'success' : actionLoading ? 'loading' : 'idle'}
        primaryActions={[
          {
            label: i18n.t('posts.communities.post.i_acknowledge'),
            onClick: handleAcknowledge,
          },
        ]}
        secondaryActions={[
          {
            label: i18n.t('posts.communities.post.acknowledge_later'),
            onClick: handleAcknowledgeLater,
          },
        ]}
      />

      {blocker.state === 'blocked' && (
        <Modal
          illustration="alert"
          title={i18n.t('posts.communities.post.close_post_title')}
          description={i18n.t('posts.communities.post.close_post_description')}
          onClose={() => blocker.reset?.()}
          disableFocusTrap
          footer={
            <>
              <F0Button
                variant="neutral"
                onClick={() => blocker.proceed?.()}
                label={i18n.t('posts.communities.post.close_post_confirm')}
              />
              <F0Button
                variant="default"
                onClick={() => blocker.reset?.()}
                label={i18n.t('posts.communities.post.continue_reading')}
              />
            </>
          }
        />
      )}
    </Box>
  )
}

export default observer(PostCard)
